import {
  configureStore as configureReduxToolkitStore,
  StoreEnhancer,
} from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import Sentry, { isSentryInitiated } from 'config/sentry';

import cookieStorage from 'helpers/cookieStorage';
import { daysDifference } from 'helpers/date';

import { AUTH_HEADERS, IS_VERIFIED } from 'store/consts';
import { AuthStateHeaders } from 'store/modules/auth';
import { initialPlayerState } from 'store/modules/player';

import { syncQueueWithAutoPlay } from './middlewares/syncQueueWithAutoPlay';
import rootReducer from './reducers';

export function setupStoreListeners(store) {
  const getAuthHeaders = () =>
    store.getState().auth.headers as AuthStateHeaders | undefined;
  const getRememberMe = () => store.getState().auth.rememberMe as boolean;

  let previousAuthHeaders = getAuthHeaders();

  store.subscribe(() => {
    const currentAuthHeaders = getAuthHeaders();

    if (!isEqual(previousAuthHeaders, currentAuthHeaders)) {
      previousAuthHeaders = currentAuthHeaders;

      if (currentAuthHeaders) {
        const daysToExpire = getExpiryDays(currentAuthHeaders);
        const expires = getRememberMe() ? daysToExpire : 7; // in days

        cookieStorage.setItem(
          AUTH_HEADERS,
          JSON.stringify(currentAuthHeaders),
          {
            expires,
          },
        );
      } else {
        cookieStorage.removeItem(AUTH_HEADERS);
      }
    }
  });
}

const getExpiryDays = ({ expiry }: Pick<AuthStateHeaders, 'expiry'>) => {
  // We don't expect expiry not to be present, but a guard will make it bulletproof
  if (isNaN(Number(expiry))) return 90;

  const timestamp = Number(expiry) * 1000;

  return daysDifference(new Date(timestamp), new Date());
};

export function getInitialState(passedState) {
  const [initialAuthHeaders, isVerified] = cookieStorage.getItems(
    [AUTH_HEADERS, IS_VERIFIED],
    // @ts-ignore
    JSON.parse,
  );

  const accessToken = initialAuthHeaders && initialAuthHeaders['access-token'];

  return {
    auth: {
      isAuthenticated: !!accessToken,
      accessToken,
      headers: {
        ...initialAuthHeaders,
      },
    },
    profile: {
      user: undefined,
      isVerified,
    },
    player: initialPlayerState,
    ...passedState,
  };
}

const stateTransformer = ({ auth, profile, player }) => ({
  auth: {
    ...auth,
    accessToken: '******',
    headers: {
      ...auth.headers,
      'access-token': '******',
      client: '******',
    },
  },
  profile: { ...profile, email: '******' },
  player,
});

function configureStore(initialState = {}) {
  const enhancers: [StoreEnhancer] | undefined = isSentryInitiated
    ? [
        Sentry.createReduxEnhancer({
          stateTransformer,
        }) as StoreEnhancer,
      ]
    : undefined;

  const persistedReducer = persistReducer(
    {
      key: 'root',
      storage,
      whitelist: [
        'onboarding_responses',
        'onboarding',
        'onboardingPlaylist',
        'editorialPlaylist',
        'quizManager',
        'promoCountdownBanner',
      ],
    },
    rootReducer,
  );

  const store = configureReduxToolkitStore({
    reducer: persistedReducer,
    preloadedState: getInitialState(initialState),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(syncQueueWithAutoPlay.middleware as any),
    enhancers,
  });

  const persistor = persistStore(store);

  setupStoreListeners(store);

  return { store, persistor };
}

export default configureStore;
